import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { type AnyExtension } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Table } from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import {
	Box,
	ToggleButtonGroup,
	ToggleButton,
	Tooltip,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import LinkIcon from "@mui/icons-material/Link";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import TableChartIcon from "@mui/icons-material/TableChart";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import DeleteIcon from "@mui/icons-material/Delete";
import GridOffIcon from "@mui/icons-material/GridOff";

/**
 * Fresh extension instances per editor. Sharing one module-level array across
 * editors lets them fight over the same plugin state, which shows up as
 * multi-second stalls while typing.
 */
const createExtensions = (hideTableControls: boolean): AnyExtension[] => {
	const extensions: AnyExtension[] = [
		StarterKit.configure({}),
		Underline.configure({}),
		Link.configure({
			openOnClick: false,
			HTMLAttributes: {
				style: "color: #1976d2; text-decoration: underline; cursor: pointer;",
			},
		}),
	];
	if (!hideTableControls) {
		extensions.push(
			Table.configure({ resizable: true }),
			TableRow,
			TableCell,
			TableHeader,
		);
	}
	return extensions;
};

interface SkillDocumentCommentBoxProps {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    /** When true, uses compact resizable layout (for PMS). When false, uses original fixed height (for Skill Set). */
    compact?: boolean;
    /** When true, hides all table-related toolbar buttons and extensions. */
    hideTableControls?: boolean;
    /** Extra content rendered at the end of the toolbar (e.g. a Reset button). */
    toolbarEnd?: React.ReactNode;
    /**
     * Keep keystrokes inside the editor and notify the parent only on blur.
     * Use this when the parent re-renders a large tree (the risk worksheet).
     */
    commitOnBlur?: boolean;
    /** Fired on the first edit of a focused session so parents can mark a row dirty once. */
    onEditing?: () => void;
    /** Lets a parent read the live HTML at save time without subscribing to each keystroke. */
    bindHtmlGetter?: (getter: (() => string) | null) => void;
}

const SkillDocumentCommentBox: React.FC<SkillDocumentCommentBoxProps> = ({
    value,
    onChange,
    placeholder = "Add a comment...",
    disabled = false,
    compact = false,
    hideTableControls = false,
    toolbarEnd,
    commitOnBlur = false,
    onEditing,
    bindHtmlGetter,
}) => {
	const [linkDialogOpen, setLinkDialogOpen] = useState(false);
	const [linkUrl, setLinkUrl] = useState("");

	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const commitOnBlurRef = useRef(commitOnBlur);
	commitOnBlurRef.current = commitOnBlur;
	const onEditingRef = useRef(onEditing);
	onEditingRef.current = onEditing;
	// Last HTML we pushed upward, or the last HTML we applied from props.
	// Skipping the echo avoids setContent (and a cursor reset) on every letter.
	const emittedRef = useRef<string | null>(null);

	const extensions = useMemo(
		() => createExtensions(hideTableControls),
		[hideTableControls]
	);

	const editorProps = useMemo(
		() => ({
			attributes: {
				"data-placeholder": placeholder,
				style: `min-height: ${compact ? 80 : 120}px; outline: none;`,
			},
		}),
		[placeholder, compact]
	);

	const editor = useEditor({
		extensions,
		content: value,
		editable: !disabled,
		editorProps,
		onUpdate: ({ editor: current }) => {
			onEditingRef.current?.();
			if (commitOnBlurRef.current) return;
			const html = current.getHTML();
			emittedRef.current = html;
			onChangeRef.current(html);
		},
		onBlur: ({ editor: current }) => {
			if (!commitOnBlurRef.current) return;
			const html = current.getHTML();
			if (html === emittedRef.current) return;
			emittedRef.current = html;
			onChangeRef.current(html);
		},
	});

	useEffect(() => {
		if (!editor || !bindHtmlGetter) return;
		bindHtmlGetter(() => editor.getHTML());
		return () => bindHtmlGetter(null);
	}, [editor, bindHtmlGetter]);

	// Apply value changes that originated outside this editor (load, reset,
	// refetch). Ignore echoes of our own onChange and ignore updates while
	// the user is typing — setContent mid-keystroke rebuilds the document.
	useEffect(() => {
		if (!editor || editor.isFocused) return;
		if (value === emittedRef.current) return;
		const current = editor.getHTML();
		if (value === current) {
			emittedRef.current = value;
			return;
		}
		emittedRef.current = value;
		editor.commands.setContent(value || "", false);
	}, [value, editor]);

	// Update editable when disabled changes
	useEffect(() => {
		if (editor) {
			editor.setEditable(!disabled);
		}
	}, [disabled, editor]);

	if (!editor) {
		return (
			<Box
				sx={{
					border: "1px solid rgba(0, 0, 0, 0.60)",
					borderRadius: 1,
					minHeight: compact ? 80 : 120,
				}}
			/>
		);
	}

    return (
        <Box
            sx={{
				border: "1px solid rgba(0, 0, 0, 0.60)",
				borderRadius: 1,
				...(compact
					? {
							resize: "vertical",
							minHeight: 80,
							maxHeight: 400,
							overflowY: "auto",
					  }
					: { overflow: "hidden" }),

				"& .ProseMirror": {
                    minHeight: compact ? 80 : 120,
					padding: 2,
                    fontSize: 14,
					overflowX: "auto",
				},
				"& .ProseMirror p.is-editor-empty:first-child::before": {
					content: "attr(data-placeholder)",
					float: "left",
					color: "#3c3c3c",
					pointerEvents: "none",
					height: 0,
				},
				"& .ProseMirror table": {
					borderCollapse: "collapse",
					width: "max-content",
					minwidth: "100%",
					margin: "8px 0",
				},
				"& .ProseMirror th, & .ProseMirror td": {
					border: "1px solid #ddd",
					padding: "8px",
					minWidth: 100,
				},
				"& .ProseMirror th": {
					backgroundColor: "#f5f5f5",
                },
            }}
        >
		{!disabled && (
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 0.5,
					p: 1,
					borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
					backgroundColor: "#fafafa",
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
					<ToggleButtonGroup size="small" sx={{ flexWrap: "wrap" }}>
						<ToggleButton
							selected={editor.isActive("bold")}
							onClick={() => editor.chain().focus().toggleBold().run()}
							value="bold"
						>
							<FormatBoldIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("italic")}
							onClick={() => editor.chain().focus().toggleItalic().run()}
							value="italic"
						>
							<FormatItalicIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("underline")}
							onClick={() => editor.chain().focus().toggleUnderline().run()}
							value="underline"
						>
							<FormatUnderlinedIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("link")}
							onClick={() => {
								const prevHref = editor.getAttributes("link").href;
								setLinkUrl(prevHref || "");
								setLinkDialogOpen(true);
							}}
							value="link"
						>
							<LinkIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("bulletList")}
							onClick={() => editor.chain().focus().toggleBulletList().run()}
							value="bullet"
						>
							<FormatListBulletedIcon fontSize="small" />
						</ToggleButton>
						<ToggleButton
							selected={editor.isActive("orderedList")}
							onClick={() => editor.chain().focus().toggleOrderedList().run()}
							value="ordered"
						>
							<FormatListNumberedIcon fontSize="small" />
						</ToggleButton>
						{!hideTableControls && (
							<>
								<Tooltip title="Insert table">
									<ToggleButton
										onClick={() =>
											editor.chain().focus().insertTable({ rows: 3, cols: 3 }).run()
										}
										value="table"
									>
										<TableChartIcon fontSize="small" />
									</ToggleButton>
								</Tooltip>
								<Tooltip title="Add row below">
									<span>
										<ToggleButton
											disabled={!editor.isActive("table")}
											onClick={() => editor.chain().focus().addRowAfter().run()}
											value="addRow"
										>
											<PlaylistAddIcon fontSize="small" />
										</ToggleButton>
									</span>
								</Tooltip>
								<Tooltip title="Add column after">
									<span>
										<ToggleButton
											disabled={!editor.isActive("table")}
											onClick={() => editor.chain().focus().addColumnAfter().run()}
											value="addColumn"
										>
											<ViewColumnIcon fontSize="small" />
										</ToggleButton>
									</span>
								</Tooltip>
								<Tooltip title="Delete row">
									<span>
										<ToggleButton
											disabled={!editor.isActive("table")}
											onClick={() => editor.chain().focus().deleteRow().run()}
											value="deleteRow"
										>
											<DeleteIcon fontSize="small" />
										</ToggleButton>
									</span>
								</Tooltip>
								<Tooltip title="Delete column">
									<span>
										<ToggleButton
											disabled={!editor.isActive("table")}
											onClick={() => editor.chain().focus().deleteColumn().run()}
											value="deleteColumn"
										>
											<DeleteIcon fontSize="small" />
										</ToggleButton>
									</span>
								</Tooltip>
								<Tooltip title="Delete table">
									<span>
										<ToggleButton
											disabled={!editor.isActive("table")}
											onClick={() => editor.chain().focus().deleteTable().run()}
											value="deleteTable"
										>
											<GridOffIcon fontSize="small" />
										</ToggleButton>
									</span>
								</Tooltip>
							</>
						)}
					</ToggleButtonGroup>
				{toolbarEnd}
			</Box>
		)}
			<EditorContent editor={editor} />
			<Dialog
				open={linkDialogOpen}
				onClose={() => setLinkDialogOpen(false)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Insert Link</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						margin="dense"
						label="URL"
						type="url"
						fullWidth
						variant="outlined"
						value={linkUrl}
						onChange={(e) => setLinkUrl(e.target.value)}
						placeholder="https://example.com"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
					<Button
					onClick={() => {
						const raw = linkUrl.trim();
						if (raw) {
							const href =
								/^(https?:\/\/|mailto:|\/\/)/i.test(raw)
									? raw
									: `https://${raw}`;
							editor.chain().focus().setLink({ href }).run();
						}
						setLinkDialogOpen(false);
						setLinkUrl("");
					}}
						variant="contained"
					>
						Insert
					</Button>
				</DialogActions>
			</Dialog>
        </Box>
    );
};

export default memo(SkillDocumentCommentBox);